import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  Heading,
  LearningContentCard,
  getLocalStorageHistory,
  setLocalStorageItems,
} from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { N800 } from '@atlaskit/theme/colors';
import { useLoaderData, useLocation } from 'react-router-dom';
import { token } from '@atlaskit/tokens';
import { throttle, times } from 'lodash';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { Sidebar } from 'src/pages/CatalogPage/children/Sidebar';
import {
  CatalogLoaderProps,
  getAtlenzResources,
  getCatalogResources,
  getFiltersFromUrl,
} from 'src/pages/CatalogPage/loader';
import { useFilters } from 'src/pages/CatalogPage/hooks/useFilters';
import { NoResults } from 'src/pages/CatalogPage/children/NoResults';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendScreenEvent, sendUIEvent } from 'src/utils/analytics';
import { MobileSidebar } from 'src/pages/CatalogPage/children/MobileSidebar';
import { HelmetContext } from 'src/components/Helmet';
import { useReferrer } from 'src/hooks/useReferrer';
import { AtlenzCatalogResource } from 'src/services/http/search';
import { ATLENZ_FF, ENABLEMENT_ACADEMY_IN_ALP_FF } from 'src/constants';
import { countAllAtlenzResources } from 'src/pages/CatalogPage/utils/atlenzTaxonomy';
import {
  fetchAdobeLearningObjects,
  transformAdobeLearningObjects,
  AdobeLearningObject,
  AdobeSearchResponse,
  getDefaultAdobeLabelToValueMap,
  ADOBE_FILTER_MAPPING,
} from 'src/services/http/adobe';

import { countAllResources, ResourceWithDurationAndType, TaxonomyCount } from './utils/taxonomy';
import { CardsList } from './children/CardsList';

export const MIN_TOTAL_ENTITIES_PER_PAGE = 12;
export const PAGE_BOTTOM_THRESHOLD = 700;
export const MAX_ENTITIES_PER_PAGE = 9;

export const CatalogPage: React.FC = () => {
  const location = useLocation();
  const { resources, taxonomy } = useLoaderData() as CatalogLoaderProps;
  const { user, isLoading: isLoadingAtlassianUser, hasError } = useAtlassianUser();
  const hasInteractedWithFilter = useRef(false);
  const hasInteractedWithSearch = useRef(false);
  const helmet = useContext(HelmetContext);
  const { referrerPath, isInitialized } = useReferrer();
  const isAtlenzAvailable = FeatureGates.checkGate(ATLENZ_FF);
  const isEnablementAcademyInAlpAvailable = FeatureGates.checkGate(ENABLEMENT_ACADEMY_IN_ALP_FF);

  const handleFilterInteraction = (): void => {
    hasInteractedWithFilter.current = true;
    // Adobe search will be triggered by URL parameter changes automatically
  };

  const handleSearchInteraction = (): void => {
    hasInteractedWithSearch.current = true;
  };

  const [numberEntitiesPerTaxonomy, setNumberEntitiesPerTaxonomy] = useState<TaxonomyCount>({});
  const [totalResources, setTotalResources] = useState<number | null>(null);

  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [isLoadingAdobeSearch, setIsLoadingAdobeSearch] = useState(false);
  const [allResources, setAllResources] = useState<
    ResourceWithDurationAndType[] | AtlenzCatalogResource[] | AdobeLearningObject[]
  >(resources);
  const [searchResults, setSearchResults] = useState<AdobeLearningObject[] | null>(null);

  const pageRef = useRef(0);

  const urlFilterParameters = useMemo(
    () => getFiltersFromUrl(globalThis?.location?.origin + location.search),
    [location.search],
  );

  const catalogIds = useMemo(() => urlFilterParameters.catalog || [], [urlFilterParameters.catalog]);
  const isAdobeCatalog = catalogIds.length > 0 && isEnablementAcademyInAlpAvailable;

  const { filteredResources, selectedFiltersLength } = useFilters(
    searchResults || allResources,
    !!searchResults || isAdobeCatalog,
  );

  const convertToAdobeFilters = useMemo(() => {
    if (!isAdobeCatalog) {
      return undefined;
    }

    const adobeFilters: { [key: string]: string[] } = {};
    const labelToValueMap = getDefaultAdobeLabelToValueMap();

    Object.keys(urlFilterParameters).forEach((key) => {
      if (key === 'search' || key === 'collectionProduct') {
        return;
      }

      const adobeKey = ADOBE_FILTER_MAPPING[key as keyof typeof ADOBE_FILTER_MAPPING] || key;
      const filterLabels = urlFilterParameters[key];

      if (key === 'catalog') {
        return;
      }

      if (labelToValueMap[key]) {
        const apiValues = filterLabels.map((label) => labelToValueMap[key][label] || label).filter((value) => value);

        if (apiValues.length > 0) {
          adobeFilters[adobeKey] = apiValues;
        }
      } else {
        adobeFilters[adobeKey] = filterLabels;
      }
    });

    return Object.keys(adobeFilters).length > 0 ? adobeFilters : undefined;
  }, [urlFilterParameters, isAdobeCatalog]);
  const [adobeNextCursor, setAdobeNextCursor] = useState<string | null>(null);

  const handleAdobeSearchResults = useCallback(
    async (searchResponse: AdobeSearchResponse | null): Promise<void> => {
      setIsLoadingAdobeSearch(false);

      if (!searchResponse) {
        if (allResources.length === 0) {
          setIsLoadingResources(true);

          try {
            const response = await fetchAdobeLearningObjects(
              catalogIds.length > 0 ? catalogIds : undefined,
              MAX_ENTITIES_PER_PAGE,
              undefined,
              convertToAdobeFilters,
            );
            const { resources: adobeResources, totalCount, hasNext } = await transformAdobeLearningObjects(response);

            setAllResources(adobeResources);
            setTotalResources(totalCount);

            if (hasNext && response.links?.next) {
              const nextUrl = new URL(response.links.next);
              const cursor = nextUrl.searchParams.get('page[cursor]');

              setAdobeNextCursor(cursor);
            } else {
              setAdobeNextCursor(null);
            }

            setSearchResults(null);
          } catch (error) {
            setAllResources([]);
            setTotalResources(0);
            setAdobeNextCursor(null);
            setSearchResults(null);
          }

          setIsLoadingResources(false);
        } else {
          setSearchResults(null);
        }

        return;
      }

      try {
        const transformed = await transformAdobeLearningObjects(searchResponse);

        setSearchResults(transformed.resources);
        if (transformed.totalCount > 0) {
          setTotalResources(transformed.totalCount);
        }
      } catch (error) {
        setSearchResults([]);
        setTotalResources(0);
      }
    },
    [allResources, catalogIds, convertToAdobeFilters],
  );

  const handleAdobeSearchStart = useCallback((): void => {
    setIsLoadingAdobeSearch(true);
  }, []);

  const isAllResourcesLoaded = typeof totalResources === 'number' && totalResources === filteredResources.length;
  const hasMoreToLoad = isAdobeCatalog ? !!adobeNextCursor : pageRef.current !== -1;
  const isLoadingClientSideFilters =
    !isAdobeCatalog && filteredResources.length < MIN_TOTAL_ENTITIES_PER_PAGE && hasMoreToLoad && !isAllResourcesLoaded;

  helmet.setHelmet({
    title: 'Learning - Atlassian Community',
    seoTags: {},
  });

  useEffect(() => {
    if (isAdobeCatalog) {
      const hasSearchParam = urlFilterParameters.search && urlFilterParameters.search.length > 0;

      if (hasSearchParam) {
        // If there's a search parameter, don't load learningObjects
        // The search will be handled by SearchbarWrapper
        setAllResources([]);
        // Don't reset totalResources to null during live search - preserve the count
        // Only reset if we don't have any search results yet
        if (!searchResults) {
          setTotalResources(null);
        }
        setAdobeNextCursor(null);
        setIsLoadingResources(false);
        pageRef.current = 0;

        return;
      }

      const loadInitialAdobeResources = async (): Promise<void> => {
        setIsLoadingResources(true);

        try {
          const response = await fetchAdobeLearningObjects(
            catalogIds.length > 0 ? catalogIds : undefined,
            MAX_ENTITIES_PER_PAGE,
            undefined,
            convertToAdobeFilters,
          );
          const { resources: adobeResources, totalCount, hasNext } = await transformAdobeLearningObjects(response);

          setAllResources(adobeResources);
          setTotalResources(totalCount);

          if (hasNext && response.links?.next) {
            const nextUrl = new URL(response.links.next);
            const cursor = nextUrl.searchParams.get('page[cursor]');

            setAdobeNextCursor(cursor);
          } else {
            setAdobeNextCursor(null);
          }
        } catch (error) {
          setAllResources([]);
          setTotalResources(0);
          setAdobeNextCursor(null);
        }

        setIsLoadingResources(false);
      };

      loadInitialAdobeResources();
    } else {
      setAllResources(resources);
      setAdobeNextCursor(null);
    }
    pageRef.current = 0;
  }, [resources, isAdobeCatalog, catalogIds, convertToAdobeFilters, urlFilterParameters, searchResults]);

  const handleCardClick = (slug: string, type: string): void => {
    const searchValue = urlFilterParameters.search ? urlFilterParameters.search[0] : '';
    const localStorageHistoryItems = getLocalStorageHistory();

    if (!localStorageHistoryItems.includes(searchValue) && !!searchValue.length) {
      setLocalStorageItems([...localStorageHistoryItems, searchValue]);
    }

    sendUIEvent(
      'catalogScreen',
      'card',
      'clicked',
      'catalogCard',
      {
        hasInteractedWithFilters: hasInteractedWithFilter.current,
        hasInteractedWithSearch: hasInteractedWithSearch.current,
        areFiltersApplied: !!selectedFiltersLength,
        isSearchEmpty: !searchValue.length,
        resourceSlug: slug,
        type,
      },
      user?.account_id,
    );
  };

  useEffect(() => {
    if (!isLoadingAtlassianUser && !hasError && resources.length > 0 && isInitialized) {
      const currentUserId = user?.account_id;

      sendScreenEvent('catalogScreen', { learningReferrerPath: referrerPath }, currentUserId);
    }
  }, [user, isLoadingAtlassianUser, hasError, resources, isInitialized, referrerPath]);

  const fetchNextPage = async (): Promise<void> => {
    setIsLoadingResources(true);
    pageRef.current++;
    const { search, ...filters } = urlFilterParameters;
    const searchValue = search && search.length ? search[0] : '';

    const nextResources = await getCatalogResources(
      false,
      filters,
      searchValue,
      pageRef.current * MAX_ENTITIES_PER_PAGE,
    );

    if (!nextResources.length) {
      pageRef.current = -1;
    }

    setAllResources(
      (prevResources: ResourceWithDurationAndType[] | AtlenzCatalogResource[] | AdobeLearningObject[]) => {
        const typedPrevResources = prevResources as ResourceWithDurationAndType[];
        const prevResourceIds = new Set(typedPrevResources.map((resource) => resource.sys.id));

        const uniqueNextResources: ResourceWithDurationAndType[] = nextResources.filter(
          (resource: ResourceWithDurationAndType) => !prevResourceIds.has(resource.sys.id),
        );

        return [...typedPrevResources, ...uniqueNextResources];
      },
    );

    setIsLoadingResources(false);
  };

  const fetchAtlenzNextPage = async (): Promise<void> => {
    setIsLoadingResources(true);
    pageRef.current++;
    const { search, ...filters } = urlFilterParameters;
    const searchValue = search && search.length ? search[0] : '';

    const nextResources = await getAtlenzResources(filters, searchValue, pageRef.current * MAX_ENTITIES_PER_PAGE);

    if (!nextResources.length) {
      pageRef.current = -1;
    }

    setAllResources(
      (prevResources: ResourceWithDurationAndType[] | AtlenzCatalogResource[] | AdobeLearningObject[]) => {
        const typedPrevResources = prevResources as AtlenzCatalogResource[];
        const prevResourceIds = new Set(typedPrevResources.map((resource: AtlenzCatalogResource) => resource.id));

        const uniqueNextResources: AtlenzCatalogResource[] = nextResources.filter(
          (resource: AtlenzCatalogResource) => !prevResourceIds.has(resource.id),
        );

        return [...typedPrevResources, ...uniqueNextResources];
      },
    );

    setIsLoadingResources(false);
  };

  const fetchAdobeNextPage = async (): Promise<void> => {
    if (!adobeNextCursor || isLoadingResources) {
      return;
    }

    setIsLoadingResources(true);

    try {
      const response = await fetchAdobeLearningObjects(
        catalogIds.length > 0 ? catalogIds : undefined,
        MAX_ENTITIES_PER_PAGE,
        adobeNextCursor,
        convertToAdobeFilters,
      );

      const { resources: nextResources, hasNext } = await transformAdobeLearningObjects(response);

      if (hasNext && response.links?.next) {
        const nextUrl = new URL(response.links.next);
        const cursor = nextUrl.searchParams.get('page[cursor]');

        setAdobeNextCursor(cursor);
      } else {
        setAdobeNextCursor(null);
        pageRef.current = -1;
      }

      if (nextResources.length > 0) {
        setAllResources(
          (prevResources: ResourceWithDurationAndType[] | AtlenzCatalogResource[] | AdobeLearningObject[]) => {
            const typedPrevResources = prevResources as AdobeLearningObject[];
            const prevResourceIds = new Set(typedPrevResources.map((resource: AdobeLearningObject) => resource.id));

            const uniqueNextResources: AdobeLearningObject[] = nextResources.filter(
              (resource: AdobeLearningObject) => !prevResourceIds.has(resource.id),
            );

            return [...typedPrevResources, ...uniqueNextResources];
          },
        );
      }
    } catch (error) {
      pageRef.current = -1;
      setAdobeNextCursor(null);
    }

    setIsLoadingResources(false);
  };

  const handleScroll = throttle((): void => {
    const { scrollTop, clientHeight, scrollHeight } = document.documentElement;

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - PAGE_BOTTOM_THRESHOLD;
    const hasMorePages = isAdobeCatalog ? !!adobeNextCursor : pageRef.current >= 0;

    const shouldFetchNextPage = isNearBottom && !isLoadingResources && hasMorePages && !isAllResourcesLoaded;

    if (shouldFetchNextPage) {
      if (isAdobeCatalog) {
        fetchAdobeNextPage();
      } else {
        isAtlenzAvailable ? fetchAtlenzNextPage() : fetchNextPage();
      }
    }
  }, 500);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [totalResources, filteredResources]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoadingClientSideFilters) {
      if (isAdobeCatalog) {
        fetchAdobeNextPage();
      } else {
        isAtlenzAvailable ? fetchAtlenzNextPage() : fetchNextPage();
      }
    }
  }, [filteredResources]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getCount = async (): Promise<void> => {
      if (isAdobeCatalog) {
        setNumberEntitiesPerTaxonomy({});

        return;
      }

      // Exclude collectionProduct and lessonProduct from the urlFilterParameters
      // as it does not affect the counters query and is needed only for the BE query
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      const { collectionProduct, lessonProduct, search, catalog, ...urlFilterParametersWithoutCollectionProduct } =
        urlFilterParameters;
      const searchValue = search && search.length ? search[0] : '';
      const resourceCount = isAtlenzAvailable
        ? await countAllAtlenzResources(urlFilterParametersWithoutCollectionProduct, searchValue)
        : await countAllResources(urlFilterParametersWithoutCollectionProduct, searchValue);

      setNumberEntitiesPerTaxonomy(resourceCount.taxonomyCount);
      setTotalResources(resourceCount.totalResources);
    };

    getCount();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [urlFilterParameters, isAdobeCatalog]);

  const isLoading = isLoadingResources || isLoadingClientSideFilters || isLoadingAdobeSearch;

  return (
    <div css={catalogSectionStyles} id="catalog-body" data-testid="catalog-page">
      <div css={sidebarContainerStyles}>
        <Sidebar
          taxonomy={taxonomy}
          numberEntitiesPerTaxonomy={numberEntitiesPerTaxonomy}
          onFilterInteraction={handleFilterInteraction}
          onSearchInteraction={handleSearchInteraction}
          isLoadingCount={totalResources === null}
          isLoadingCatalogResources={isLoading}
          isAdobeCatalog={isAdobeCatalog}
          onAdobeSearchResults={handleAdobeSearchResults}
          onAdobeSearchStart={handleAdobeSearchStart}
        />
      </div>
      <main data-testid="main-catalog-page" css={mainStyles} role="main">
        <div css={contentContainerStyles}>
          <Heading level={2}>Learning catalog</Heading>
          <div css={resultsContainerStyles} data-testid="catalog-total-container">
            <span css={filteredResourcesCounterWrapperStyles} data-testid="catalog-total-text">
              <span css={filteredResourcesCounterStyles}>
                {typeof totalResources === 'number' ? totalResources : ' '}
              </span>
              <span css={resultStyles}>{typeof totalResources === 'number' ? 'results' : ' '}</span>
            </span>
            <div css={mobileSidebarContainerStyles}>
              <MobileSidebar
                selectedFiltersLength={selectedFiltersLength}
                taxonomy={taxonomy}
                numberEntitiesPerTaxonomy={numberEntitiesPerTaxonomy}
                onFilterInteraction={handleFilterInteraction}
                onSearchInteraction={handleSearchInteraction}
                isLoadingCount={totalResources === null}
                isLoadingCatalogResources={isLoading}
                isAdobeCatalog={isAdobeCatalog}
              />
            </div>
          </div>
        </div>
        {filteredResources.length > 0 || isLoading ? (
          <div css={courseListStyles}>
            <CardsList resources={filteredResources} handleCardClick={handleCardClick} />
            {isLoading &&
              times(MIN_TOTAL_ENTITIES_PER_PAGE / 2, (i) => (
                <LearningContentCard
                  key={`loading-tile-${i}`}
                  title=""
                  link=""
                  tags={[]}
                  onClick={() => {}}
                  isLoading
                />
              ))}
          </div>
        ) : (
          <NoResults />
        )}
      </main>
    </div>
  );
};

const catalogSectionStyles = css({
  display: 'flex',
  maxWidth: '1254px',
  padding: '0 16px',
  margin: '172px auto 100px',
  justifyContent: 'space-between',
  gap: '32px',

  '@media (min-width: 768px) and (max-width: 1279px)': {
    margin: '48px auto 100px',
  },

  '@media (max-width: 767px)': {
    gap: 0,
    margin: '48px auto 100px',
  },
});

const sidebarContainerStyles = css({
  position: 'relative',
  flexShrink: 0,
  flexGrow: 0,

  [media.below.md]: {
    display: 'none',
  },
});

const mainStyles = css({
  position: 'relative',
  flexGrow: 1,
});

const contentContainerStyles = css({
  marginBottom: token('space.400'),
});

const mobileSidebarContainerStyles = css({
  display: 'none',

  [media.below.md]: {
    display: 'block',
  },
});

const resultsContainerStyles = css({
  color: N800,
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '28px',
});

const resultStyles = css({
  marginLeft: '3px',
  color: token('color.text.subtlest'),

  [media.below.md]: {
    fontSize: '16px',
    lineHeight: '22px',
  },
});

const filteredResourcesCounterStyles = css({
  [media.below.md]: {
    fontSize: '16px',
    lineHeight: '22px',
  },
});

const filteredResourcesCounterWrapperStyles = css({
  display: 'block',
});

const courseListStyles = css({
  display: 'grid',
  gap: '16px',
  gridTemplateColumns: 'repeat(auto-fill, minmax(292px, 1fr))',

  [media.above.lg]: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
});
